import * as authService from './authService'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

const BASE = '/api/v1/scratch-lottery'

/** สถานะการ์ดหวยขูด — ยังไม่เปิด = purchased/pending, เปิดแล้ว = revealed */
export type ScratchPurchaseStatus =
  | 'purchased'
  | 'pending'
  | 'revealed'
  | 'cancelled'
  | 'expired'
  | string

export interface ScratchPlayConfig {
  /** แสดงทางเข้าให้ผู้เล่นหรือไม่ (master switch + ยังมีชุดเปิดขาย) */
  userEntryVisible: boolean
  /** มีชุดเปิดขายและใบเหลือให้ซื้อหรือไม่ */
  salesOpen?: boolean
  productId?: string
  name?: string
  description?: string
  status?: string
  /** ราคาต่อการ์ด (บาท) */
  ticketPrice: number
  currency?: string
  /** ธีมการ์ด เช่น mega / set-for-life / 50x — ใช้เลือกโทน */
  theme?: string
  themeLabel?: string
  /** จำนวนช่องเลขนำโชค (ดาวดำ) */
  winningSlotCount?: number
  /** จำนวนช่องเลขของคุณ / ช่องขูด (ดาวขาว) */
  playSlotCount?: number
  /** ข้อความการันตี เช่น "ทุกใบมีรางวัล" (ไม่การันตีกำไร) */
  everyTicketWins?: boolean
  /** เปอร์เซ็นต์ที่ต้องขูดก่อนระบบจะเปิดผลอัตโนมัติ (0-1) */
  revealThreshold?: number
  /** รางวัลขั้นต่ำ (บาท) — ใช้ประกอบยอดโชว์บนการ์ด */
  minPrize?: number
  /** แจ็คพอตสูงสุด (บาท) — ใช้ประกอบยอดโชว์บนการ์ด */
  maxJackpot?: number
  /** จำนวนใบที่ยังซื้อได้ */
  availableTickets?: number
  /** จำนวนชุดที่ยังเปิดขาย */
  activeBatches?: number
  /** เกจแจ๊คพอต: ซื้อครบ N ใบ → ใบรางวัลสูงสุดที่เหลือ */
  jackpotGaugeTickets?: number
  /** เปิด/ปิดการแจกจากเกจ */
  jackpotGaugeEnabled?: boolean
}

export interface ScratchCatalogItem {
  productId: string
  name: string
  description?: string
  sortOrder?: number
  status?: string
  ticketPrice: number
  theme?: string
  winningSlotCount?: number
  playSlotCount?: number
  minPrize?: number
  maxJackpot?: number
  everyTicketWins?: boolean
  availableTickets: number
  activeBatches: number
  salesOpen: boolean
}

export interface ScratchCatalog {
  userEntryVisible: boolean
  items: ScratchCatalogItem[]
}

/** ตัวเลขในช่องหนึ่ง ๆ ของการ์ด (backend อาจส่งมาเพื่อความสมจริง — optional) */
export interface ScratchCell {
  index: number
  value: number | string
  /** รางวัลจริงหลังเปิดผล (เฉพาะช่องที่ match) */
  prizeAmount?: number
  /**
   * ยอดโชว์บนการ์ด — ช่องหลอกใช้เพื่อความสมจริง
   * ช่องที่ match หลัง reveal จะใช้ยอดที่รวมกันได้เท่า prizeAmount ของตั๋ว
   */
  displayPrizeAmount?: number
  matched?: boolean
}

export interface ScratchPurchase {
  purchaseId: string
  productId?: string | null
  productName?: string
  ticketPrice: number
  theme?: string
  status: ScratchPurchaseStatus
  currency?: string
  createdAt?: string | null
  purchasedAt?: string | null
  revealedAt?: string | null
  /** ข้อมูลรางวัลจะมีก็ต่อเมื่อเปิดผลแล้วเท่านั้น */
  prizeAmount?: number | null
  /** หน้าการ์ด (ถ้า core ส่งมา) — เลขรางวัล + ช่องเลขของคุณ */
  winningNumbers?: Array<number | string>
  cells?: ScratchCell[]
}

export interface ScratchBalance {
  before?: number | null
  after?: number | null
}

export interface ScratchRevealResult {
  purchaseId: string
  prizeAmount: number
  ticketPrice?: number
  currency?: string
  status: ScratchPurchaseStatus
  balance?: ScratchBalance | null
  balanceAfter?: number | null
  winningNumbers?: Array<number | string>
  cells?: ScratchCell[]
}

export interface ScratchJackpotGauge {
  enabled: boolean
  current: number
  target: number
  nextIsJackpot: boolean
}

export interface ScratchPlayerStats {
  ticketsBought: number
  pendingCount: number
  revealedCount: number
  totalSpent: number
  totalPrizes: number
  netProfit: number
  profitCount: number
  breakEvenCount: number
  lossCount: number
  currentLossStreak: number
  productId?: string | null
  jackpotGauge?: ScratchJackpotGauge
}

function headers(extra?: Record<string, string>) {
  const token = authService.getToken()
  const tenantId = getRuntimeTenantIdSync()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    ...(extra || {}),
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message || `HTTP ${response.status}`,
    ) as Error & { code?: string; status?: number }
    err.code = (data as { code?: string })?.code
    err.status = response.status
    throw err
  }
  return data as T
}

export async function fetchScratchPlayConfig() {
  const response = await fetch(withApiBaseUrl(`${BASE}/play-config`), {
    headers: headers(),
  })
  return parseJson<{ success: boolean; data: ScratchPlayConfig }>(response)
}

export async function fetchScratchCatalog() {
  const response = await fetch(withApiBaseUrl(`${BASE}/catalog`), {
    headers: headers(),
  })
  return parseJson<{ success: boolean; data: ScratchCatalog }>(response)
}

export async function fetchScratchProductPlayConfig(productId: string) {
  const response = await fetch(
    withApiBaseUrl(`${BASE}/products/${encodeURIComponent(productId)}/play-config`),
    { headers: headers() },
  )
  return parseJson<{ success: boolean; data: ScratchPlayConfig }>(response)
}

/**
 * ซื้อการ์ดหวยขูด — ส่ง idempotencyKey ทั้งใน body และ header `Idempotency-Key`
 * เพื่อให้ retry ด้วย key เดิมไม่สร้างการ์ดซ้ำ
 */
export async function purchaseScratchTicket(idempotencyKey: string, productId?: string) {
  const response = await fetch(withApiBaseUrl(`${BASE}/purchases`), {
    method: 'POST',
    headers: headers({ 'Idempotency-Key': idempotencyKey }),
    body: JSON.stringify({
      idempotencyKey,
      ...(productId ? { productId } : {}),
    }),
  })
  return parseJson<{ success: boolean; data: ScratchPurchase }>(response)
}

export async function revealScratchPurchase(purchaseId: string) {
  const response = await fetch(
    withApiBaseUrl(`${BASE}/purchases/${encodeURIComponent(purchaseId)}/reveal`),
    {
      method: 'POST',
      headers: headers(),
    },
  )
  return parseJson<{ success: boolean; data: ScratchRevealResult }>(response)
}

export async function fetchMyScratchPurchases(params?: {
  page?: number
  limit?: number
  productId?: string
}) {
  const page = params?.page || 1
  const limit = params?.limit || 20
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (params?.productId) qs.set('productId', params.productId)
  const response = await fetch(withApiBaseUrl(`${BASE}/purchases/my?${qs}`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data: ScratchPurchase[]
    pagination?: { page: number; limit: number; total: number; totalPages: number }
  }>(response)
}

export async function fetchMyScratchStats(productId?: string | null) {
  const qs = new URLSearchParams()
  if (productId) qs.set('productId', productId)
  const suffix = qs.toString() ? `?${qs}` : ''
  const response = await fetch(withApiBaseUrl(`${BASE}/stats/my${suffix}`), {
    headers: headers(),
  })
  return parseJson<{ success: boolean; data: ScratchPlayerStats }>(response)
}

/** การ์ดที่ยังไม่เปิดผลและพร้อมขูดต่อได้ — เฉพาะสถานะที่จ่ายเงินแล้ว */
const REVEALABLE_STATUSES = new Set(['paid', 'revealing', 'credit_uncertain', 'purchased', 'pending'])

export function isScratchPurchasePending(purchase: ScratchPurchase): boolean {
  const status = String(purchase.status || '').toLowerCase()
  if (!REVEALABLE_STATUSES.has(status)) return false
  return purchase.revealedAt == null
}

export function classifyScratchNet(prizeAmount: number, ticketPrice: number) {
  const net = Number(prizeAmount || 0) - Number(ticketPrice || 0)
  if (net > 0) return { net, kind: 'profit' as const, label: 'กำไรสุทธิ' }
  if (net === 0) return { net, kind: 'breakEven' as const, label: 'คุ้มทุน' }
  return { net, kind: 'loss' as const, label: 'ขาดทุนสุทธิ' }
}
